import type { DrawPoint } from "@/lib/presentation-draw-path";
import { easeInOutSine } from "@/lib/sketch-stroke/sketch-easing";
import { strokePrefix } from "@/lib/sketch-stroke/stroke-points";
import { prefersReducedMotion } from "@/lib/reveal-motion";

export interface PresentationPathFrame {
  points: DrawPoint[];
  tip: DrawPoint;
  /** Pen progress eased slow-fast-slow; exactly 1 on the final frame. */
  progress: number;
  frame: number;
}

export function animatePresentationPath(
  points: DrawPoint[],
  durationMs: number,
  signal: AbortSignal,
  onFrame: (frame: PresentationPathFrame) => void,
): Promise<boolean> {
  if (signal.aborted || points.length < 2) return Promise.resolve(false);
  const reducedMotion = prefersReducedMotion();
  if (reducedMotion || durationMs <= 0) {
    onFrame({
      points,
      tip: points[points.length - 1]!,
      progress: 1,
      frame: 1,
    });
    return Promise.resolve(true);
  }
  return new Promise((resolve) => {
    let requestId = 0;
    let frame = 0;
    const startedAt = performance.now();
    const finish = (completed: boolean) => {
      signal.removeEventListener("abort", onAbort);
      if (requestId) cancelAnimationFrame(requestId);
      resolve(completed);
    };
    const onAbort = () => finish(false);
    const tick = (now: number) => {
      if (signal.aborted) return;
      const progress = easeInOutSine((now - startedAt) / durationMs);
      const visible = strokePrefix(points, progress);
      frame += 1;
      onFrame({
        points: visible,
        tip: visible[visible.length - 1]!,
        progress,
        frame,
      });
      if (progress >= 1) {
        finish(true);
        return;
      }
      requestId = requestAnimationFrame(tick);
    };
    signal.addEventListener("abort", onAbort, { once: true });
    requestId = requestAnimationFrame(tick);
  });
}
